import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { Role } from '../../model/auth/Role';
import { Region } from '../../model/location/Region';
import { Observable, of } from 'rxjs';
import { LocationService } from '../../service/location.service';
import { Locale } from '../../model/Locale';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { School, SchoolGrade, SchoolGradeEx, SchoolType } from '../../model/location/School';
import { ItemAutocompleteComponent } from '../../common/item-autocomplete/item-autocomplete.component';
import { tap } from 'rxjs/operators';
import { ContestTaskType, localizeContestTaskType } from '../../model/contest/ContestTask';

export interface SchoolSelectResult {
  school?: School;
  type: SchoolType;
  region: Region;
  grade?: SchoolGrade;
}

@Component({
  selector: 'school-select',
  templateUrl: './school-select.component.html',
  styleUrls: ['./school-select.component.scss']
})
export class SchoolSelectComponent implements OnInit {

  @Input() roles: Role[] = [];

  @Input() selectedRegion?: Region;
  @Input() selectedSchoolType?: SchoolType;

  @Input() set selectedGrade(grade: SchoolGrade | undefined) {
    this.selectedGradeEx = grade
      ? new SchoolGradeEx(grade, this.showGradeType ? ContestTaskType.standard : undefined)
      : undefined;
  }

  selectedGradeEx?: SchoolGradeEx;

  @Input() selectedSchool?: School;

  @Input() showGrade = true;
  @Input() showGradeType = false;
  @Input() showRegion = true;
  @Input() showSchool = true;
  @Input() showSchoolType = true;
  @Input() allowGradeSelect = true;
  @Input() allowRegionSelect = true;
  @Input() allowSchoolTypeSelect = true;
  @Input() allowSchoolSelect = true;

  @Input() requireGradeSelect = false;

  @Input() clearSchoolAfterSelect = true;
  @Input() allowSchoolClear = false;
  @Input() schoolRequired = true;

  @Input() addAllRegionsOption = false;

  @Output() gradeSelected = new EventEmitter<SchoolGradeEx>();
  @Output() regionSelected = new EventEmitter<Region>();
  @Output() schoolSelected = new EventEmitter<SchoolSelectResult>();

  private schoolSearch?: ItemAutocompleteComponent<School>;

  @ViewChild('schoolSearch', {static: false}) set content(content: ItemAutocompleteComponent<School>) {
    if (content) {
      this.schoolSearch = content;

      if (this.selectedSchool) {
        this.schoolSearch.setValue(this.selectedSchool);
      }
      if (this.allowSchoolSelect && this.regionField.value && (!this.schoolField.value || !this.allowSchoolClear)) {
        this.schoolSearch.enable();
      } else {
        this.schoolSearch.disable();
      }

      this.cdRef.detectChanges();
    }
  }

  regionField = new FormControl('', [
    Validators.required
  ]);
  typeField = new FormControl('', [
    Validators.required
  ]);
  gradeField = new FormControl('', [
    Validators.required
  ]);
  schoolField = new FormControl('', []);
  selectForm = new FormGroup({
    region: this.regionField,
    type: this.typeField,
    grade: this.gradeField,
    school: this.schoolField
  });

  loadRegions = (): Observable<Region[]> => {
    return this.locationService.getRegions(this.locale as Locale)
      .pipe(tap(regions => {

        if (this.addAllRegionsOption) {
          regions.unshift(new Region('-1', $localize`:General|All Regions term@@general-all-regions-term:All regions`));
        }

        return regions;
      }));
  };
  loadTypes = (): Observable<SchoolType[]> => {
    return this.locationService.getSchoolTypes(this.locale as Locale)
      .pipe(tap(types => {

        types.unshift(new SchoolType('-1', $localize`:General|All School Types term@@general-all-school-types-term:All types`));

        return types;
      }));
  };
  loadGrades = (): Observable<SchoolGradeEx[]> => {
    return this.showGradeType
      ? of([
        new SchoolGradeEx(SchoolGrade.second, ContestTaskType.standard),
        new SchoolGradeEx(SchoolGrade.third, ContestTaskType.standard),
        new SchoolGradeEx(SchoolGrade.forth, ContestTaskType.standard),
        new SchoolGradeEx(SchoolGrade.fifth, ContestTaskType.standard),
        new SchoolGradeEx(SchoolGrade.sixth, ContestTaskType.standard),
        new SchoolGradeEx(SchoolGrade.seventh, ContestTaskType.standard),
        new SchoolGradeEx(SchoolGrade.eighth, ContestTaskType.standard),
        new SchoolGradeEx(SchoolGrade.ninth, ContestTaskType.standard),
        new SchoolGradeEx(SchoolGrade.tenth, ContestTaskType.standard),
        new SchoolGradeEx(SchoolGrade.tenth, ContestTaskType.advanced),
        new SchoolGradeEx(SchoolGrade.eleventh, ContestTaskType.standard),
        new SchoolGradeEx(SchoolGrade.eleventh, ContestTaskType.advanced)
      ])
      : of([
        new SchoolGradeEx(SchoolGrade.second),
        new SchoolGradeEx(SchoolGrade.third),
        new SchoolGradeEx(SchoolGrade.forth),
        new SchoolGradeEx(SchoolGrade.fifth),
        new SchoolGradeEx(SchoolGrade.sixth),
        new SchoolGradeEx(SchoolGrade.seventh),
        new SchoolGradeEx(SchoolGrade.eighth),
        new SchoolGradeEx(SchoolGrade.ninth),
        new SchoolGradeEx(SchoolGrade.tenth),
        new SchoolGradeEx(SchoolGrade.eleventh)
      ]);
  };
  searchSchools = (query: string): Observable<School[]> => {
    const region = this.regionField.value as Region;
    const type = this.showSchoolType ? this.typeField.value as SchoolType || '' : new SchoolType('-1', '');

    return region && query
      ? this.locationService.searchSchools(region.id, type.id, query.toLocaleLowerCase(this.locale))
      : of([]);
  };

  typeSelected = (type: SchoolType) => {
    if ((this.typeField.value && !type) || (this.typeField.value && (this.typeField.value as SchoolType).id != type.id)) {
      this.schoolField.setValue(undefined);
      if (this.schoolSearch) this.schoolSearch.clearInput(true, false);
    }

    this.typeField.setValue(type);

    if (this.allowSchoolSelect && (!this.schoolField.value || !this.allowSchoolClear)) {
      this.schoolSearch?.enable();
    } else {
      this.schoolSearch?.disable();
    }
  };

  constructor(private locationService: LocationService,
              private cdRef: ChangeDetectorRef,
              @Inject(LOCALE_ID) private locale: string) {
  }

  ngOnInit(): void {
    if (this.selectedRegion) this.regionField.setValue(this.selectedRegion);
    if (this.selectedSchoolType) this.typeField.setValue(this.selectedSchoolType);
    if (this.selectedGrade) this.gradeField.setValue(this.selectedGrade);
    if (this.selectedSchool) this.schoolField.setValue(this.selectedSchool);
  }

  compareRegions(r1?: Region, r2?: Region): boolean {
    return r1 != undefined && r2 != undefined && r1.id === r2.id;
  }

  displayRegion(region: Region): string {
    return region.name;
  }

  handleRegionSelected(region: Region) {
    if ((this.regionField.value && !region) || (this.regionField.value && (this.regionField.value as Region).id != region.id)) {
      this.regionField.setValue(region);

      this.selectedSchoolType = undefined;
      this.typeField.setValue(undefined);

      this.schoolField.setValue(undefined);
      if (this.schoolSearch) this.schoolSearch.clearInput(true, false);

      if (this.allowSchoolSelect && (!this.schoolField.value || !this.allowSchoolClear)) {
        const region = this.regionField.value as Region;
        if (region.id == '-1') {
          this.schoolSearch?.disable();
        } else {
          this.schoolSearch?.enable();
        }
      } else {
        this.schoolSearch?.disable();
      }
    } else {
      this.regionField.setValue(region);

      if (this.allowSchoolSelect && (!this.schoolField.value || !this.allowSchoolClear)) {
        this.schoolSearch?.enable();
      } else {
        this.schoolSearch?.disable();
      }
    }

    this.regionSelected.emit(region);
  }

  compareTypes(t1?: SchoolType, t2?: SchoolType): boolean {
    return t1 != undefined && t2 != undefined && t1.id === t2.id;
  }

  displayType(type: SchoolType): string {
    return type.name;
  }

  compareGrades(g1?: SchoolGradeEx, g2?: SchoolGradeEx): boolean {
    return g1 != undefined && g2 != undefined && g1.grade == g2.grade && g1.type == g2.type;
  }

  displayGrade(grade: SchoolGradeEx): string {
    return grade.type && grade.type != ContestTaskType.standard
      ? `${grade.grade} (${localizeContestTaskType(grade.type)})`
      : grade.grade;
  }

  handleGradeSelected(grade: SchoolGradeEx) {
    this.gradeField.setValue(grade);
    this.gradeSelected.emit(grade);
  }

  displaySchool(school?: School): string {
    return school
      ? school.short_name ? `${school.short_name} (${school.name})` : school.name
      : '';
  };

  handleSchoolSelected(school: School) {
    if (!this.typeField.value || (this.typeField.value as SchoolType).id == '-1') {
      this.selectedSchoolType = new SchoolType(school.type, '');
      this.typeField.setValue(this.selectedSchoolType);
    }
    this.schoolField.setValue(school);

    if (school && this.regionField.value) {
      this.schoolSelected.emit({
        school: school,
        type: this.typeField.value as SchoolType,
        region: this.regionField.value as Region,
        grade: (this.gradeField.value as SchoolGradeEx).grade
      });
    }

    if (!this.clearSchoolAfterSelect && this.allowSchoolClear) {
      this.schoolSearch?.disable();
    }
  }

  handleSchoolClear() {
    this.schoolField.setValue(undefined);
    this.schoolSelected.emit({
      school: undefined,
      type: this.typeField.value as SchoolType,
      region: this.regionField.value as Region,
      grade: (this.gradeField.value as SchoolGradeEx).grade
    });
    this.schoolSearch?.enable();
  }
}
